import { Box, Card, CardContent, Typography } from '@mui/material'
import type { SimulationInput, SimulationResults } from '../../api/types'
import { formatEUR2 } from '../../format'
import DecimalInput from './DecimalInput'
import ComputedField from './ComputedField'
import { acquisitionFields, type FieldKey } from './formFields'
import NumberField from './NumberField'

const fieldByKey = Object.fromEntries(acquisitionFields.map((f) => [f.key, f])) as Record<
  (typeof acquisitionFields)[number]['key'],
  (typeof acquisitionFields)[number]
>

export default function AcquisitionCard({
  form,
  preview,
  pricePerM2,
  setPricePerM2,
  setNum,
}: {
  form: SimulationInput
  preview: SimulationResults
  pricePerM2: number
  setPricePerM2: (value: number) => void
  setNum: (key: FieldKey, raw: string) => void
}) {
  return (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Acquisition
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          }}
        >
          {/* Extra decimals so digits can still be appended after the value was derived from prix / m². */}
          <NumberField
            field={fieldByKey.purchasePrice}
            form={form}
            setNum={setNum}
            size="medium"
            decimals={4}
          />
          <DecimalInput
            label="Prix / m²"
            unit="€/m²"
            decimals={4}
            value={pricePerM2}
            onChange={setPricePerM2}
          />
          <NumberField
            field={fieldByKey.renovationCosts}
            form={form}
            setNum={setNum}
            size="medium"
          />
          <ComputedField
            label="Coût de l'investissement"
            value={preview.investmentCost === 0 ? '' : formatEUR2(preview.investmentCost)}
            hint="Prix d'achat + frais de notaire + travaux + frais supplémentaires"
          />

          <NumberField field={fieldByKey.notaryFeeRate} form={form} setNum={setNum} size="medium" />
          <ComputedField
            label="Montant frais de notaire"
            value={preview.notaryFeeAmount === 0 ? '' : formatEUR2(preview.notaryFeeAmount)}
            hint="Prix d'achat × taux de notaire"
          />
          <NumberField field={fieldByKey.compromis} form={form} setNum={setNum} size="medium" />
          <ComputedField
            label="Montant de l'apport"
            value={form.downPayment === 0 ? '' : formatEUR2(form.downPayment)}
            hint="Frais de notaire + frais supplémentaires"
          />
        </Box>
      </CardContent>
    </Card>
  )
}
